import { useState } from 'react';
import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';

interface ExportButtonProps {
  /** Drives the icon, the default label, and the accessible name. */
  format: 'excel' | 'pdf';
  /** Runs the export. Rejections are surfaced inline next to the button. */
  onExport: () => Promise<void>;
  label?: string;
  disabled?: boolean;
  disabledReason?: string;
  testId?: string;
}

const DEFAULT_LABELS: Record<ExportButtonProps['format'], string> = {
  excel: 'Export to Excel',
  pdf: 'Export to PDF',
};

/**
 * Shared export trigger used by every non-form landing page.
 *
 * Owns the busy state and error reporting so each page only has to supply the
 * data-building callback.
 */
export function ExportButton({
  format,
  onExport,
  label,
  disabled = false,
  disabledReason,
  testId,
}: ExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const text = label ?? DEFAULT_LABELS[format];
  const Icon = format === 'excel' ? FileSpreadsheet : FileText;
  const isDisabled = disabled || isExporting;

  async function handleClick() {
    setError(null);
    setIsExporting(true);
    try {
      await onExport();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div
      style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
      data-html2canvas-ignore="true"
    >
      <button
        type="button"
        onClick={() => void handleClick()}
        disabled={isDisabled}
        data-testid={testId ?? `export-${format}`}
        aria-label={text}
        title={disabled ? disabledReason ?? text : text}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 14px',
          backgroundColor: '#fff',
          color: isDisabled ? '#9ca3af' : '#1a3bcc',
          border: `1px solid ${isDisabled ? '#d1d5db' : '#1a3bcc'}`,
          borderRadius: '4px',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          fontSize: '0.85rem',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          transition: 'all 0.2s',
        }}
      >
        {isExporting ? (
          <Loader2 size={15} style={{ animation: 'brg-export-spin 1s linear infinite' }} />
        ) : (
          <Icon size={15} />
        )}
        {isExporting ? 'Exporting…' : text}
      </button>

      {error && (
        <span role="alert" data-testid={`${testId ?? `export-${format}`}-error`} style={{ fontSize: '0.75rem', color: '#c62828' }}>
          {error}
        </span>
      )}

      <style>{`@keyframes brg-export-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
